import React, { useCallback, useEffect, useRef, useState } from "react";
import useStore from "../Store";
import ToDoApi from "../API/ToDoApi";
import Camera from "../Camera/camera"; // Оставляем твой Camera.jsx без изменений

import TaskFilters from "./TaskFilters";
import TaskList from "./TaskList";
import NewTaskModal from "./NewTaskModal";
import CompleteTaskModal from "./CompleteTaskModal";
import PhotoLightbox from "./PhotoLightbox";
import { MainDiv, Header, Title, Button } from "./design";

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Подготовка файлов здесь, без отдельного utils/photos.js
const compressImage = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Fehler beim Lesen der Datei"));
    reader.onload = () => {
        const image = new Image();
        image.onerror = () => reject(new Error("Bild konnte nicht geöffnet werden"));
        image.onload = () => {
            const scale = Math.min(1, 1200 / Math.max(image.width, image.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(image.width * scale));
            canvas.height = Math.max(1, Math.round(image.height * scale));
            canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
                blob => blob ? resolve(blob) : reject(new Error("Komprimierung fehlgeschlagen")),
                "image/jpeg", 0.8
            );
        };
        image.src = reader.result;
    };
    reader.readAsDataURL(file);
});

const emptyForm = (house = "") => ({
    house, comment: "", author: "", date: today()
});

const ToDo = () => {
    const houses = useStore(state => state.Houses.Location1) ?? [];
    const [tasks, setTasks] = useState([]);
    const [selectedHouse, setSelectedHouse] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [completionTask, setCompletionTask] = useState(null);
    const [form, setForm] = useState(() => emptyForm());
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraTarget, setCameraTarget] = useState("create");
    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [createPhotos, setCreatePhotos] = useState([]);
    const [completionPhotos, setCompletionPhotos] = useState([]);
    const [photoProcessing, setPhotoProcessing] = useState(false);
    const photoBusyRef = useRef(false);
    const createRef = useRef([]);
    const completionRef = useRef([]);
    const photoUrlsRef = useRef(new Set());

    useEffect(() => {
        return () => {
            photoUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
            photoUrlsRef.current.clear();
        };
    }, []);

    function setPhotoTarget(target, next) {
        if (target === "create") {
            createRef.current = next;
            setCreatePhotos(next);
        } else {
            completionRef.current = next;
            setCompletionPhotos(next);
        }
    }

    function clearPhotos(target) {
        const items = target === "create" ? createRef.current : completionRef.current;
        items.forEach(({ preview }) => {
            URL.revokeObjectURL(preview);
            photoUrlsRef.current.delete(preview);
        });
        setPhotoTarget(target, []);
    }

    function removePhoto(target, index) {
        const items = target === "create" ? createRef.current : completionRef.current;
        const removed = items[index];
        if (!removed || photoBusyRef.current) return;
        URL.revokeObjectURL(removed.preview);
        photoUrlsRef.current.delete(removed.preview);
        setPhotoTarget(target, items.filter((_, i) => i !== index));
    }

    async function addPhotos(files, target) {
        const selected = Array.from(files ?? []);
        if (!selected.length || photoBusyRef.current) return;
        const current = target === "create" ? createRef.current : completionRef.current;
        if (current.length + selected.length > 12) {
            throw new Error("Maximal 12 Fotos pro Anfrage.");
        }
        if (selected.some(file => !file.type.startsWith("image/"))) {
            throw new Error("Bitte nur Bilder auswählen.");
        }
        photoBusyRef.current = true;
        setPhotoProcessing(true);
        try {
            const blobs = await Promise.all(selected.map(compressImage));
            const additions = blobs.map(blob => {
                const preview = URL.createObjectURL(blob);
                photoUrlsRef.current.add(preview);
                return { blob, preview };
            });
            const latest = target === "create" ? createRef.current : completionRef.current;
            setPhotoTarget(target, [...latest, ...additions]);
        } finally {
            photoBusyRef.current = false;
            setPhotoProcessing(false);
        }
    }

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError("");
        ToDoApi.getTasks(selectedHouse, statusFilter, controller.signal)
            .then(result => {
                if (!controller.signal.aborted) setTasks(result.Tasks ?? []);
            })
            .catch(err => {
                if (!controller.signal.aborted) setError(err.message);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [selectedHouse, statusFilter]);

    async function reloadTasks() {
        const result = await ToDoApi.getTasks(selectedHouse, statusFilter);
        setTasks(result.Tasks ?? []);
    }

    function openNewTask() {
        clearPhotos("create");
        setForm(emptyForm(selectedHouse || houses[0] || ""));
        setModalOpen(true);
    }

    function closeNewTask() {
        if (saving || photoProcessing) return;
        clearPhotos("create");
        setModalOpen(false);
    }

    const closePreview = useCallback(() => setPreviewPhoto(null), []);

    async function handleFiles(files, target) {
        try {
            await addPhotos(files, target);
        } catch (err) {
            alert(err.message);
        }
    }

    function openCamera(target) {
        setCameraTarget(target);
        setCameraOpen(true);
    }

    function receiveCameraPhoto(blob) {
        if (!blob) return;
        const file = new File([blob], "camera.png", {
            type: blob.type || "image/png"
        });
        void handleFiles([file], cameraTarget);
    }

    async function saveTask(event) {
        event.preventDefault();
        if (saving || photoProcessing || !createPhotos.length ||
            !form.house || !form.date || !form.comment.trim() || !form.author.trim()) return;
        setSaving(true);
        try {
            await ToDoApi.addTask({
                ...form,
                comment: form.comment.trim(),
                author: form.author.trim()
            }, createPhotos);
            clearPhotos("create");
            setModalOpen(false);
            await reloadTasks();
        } catch (err) {
            alert("Fehler beim Speichern: " + err.message);
        } finally {
            setSaving(false);
        }
    }

    function closeCompletion() {
        if (saving || photoProcessing) return;
        clearPhotos("completion");
        setCompletionTask(null);
    }

    async function changeStatus(task) {
        if (!task.completed) {
            clearPhotos("completion");
            setCompletionTask(task);
            return;
        }
        if (!window.confirm("Aufgabe wieder öffnen?")) return;
        setSaving(true);
        try {
            await ToDoApi.reopenTask(task.id);
            await reloadTasks();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function completeTask() {
        if (!completionTask || !completionPhotos.length || saving || photoProcessing) return;
        setSaving(true);
        try {
            await ToDoApi.completeTask(completionTask.id, completionPhotos);
            clearPhotos("completion");
            setCompletionTask(null);
            await reloadTasks();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function deleteTask(task) {
        if (!window.confirm("Aufgabe und alle Fotos löschen?")) return;
        setSaving(true);
        try {
            await ToDoApi.deleteTask(task.id);
            await reloadTasks();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <MainDiv>
            <Header>
                <Title>Aufgaben</Title>
                <Button type="button" onClick={openNewTask} disabled={saving}>+ Neue Aufgabe</Button>
            </Header>

            <TaskFilters
                houses={houses}
                selectedHouse={selectedHouse}
                setSelectedHouse={setSelectedHouse}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />

            <TaskList
                tasks={tasks}
                loading={loading}
                error={error}
                busy={saving}
                onPreview={setPreviewPhoto}
                onStatusChange={changeStatus}
                onDelete={deleteTask}
            />

            {modalOpen && (
                <NewTaskModal
                    houses={houses}
                    form={{ ...form, photos: createPhotos }}
                    setForm={setForm}
                    saving={saving}
                    photoProcessing={photoProcessing}
                    onClose={closeNewTask}
                    onSave={saveTask}
                    onPhotos={handleFiles}
                    onOpenCamera={openCamera}
                    onRemovePhoto={removePhoto}
                    onPreview={setPreviewPhoto}
                />
            )}

            {completionTask && (
                <CompleteTaskModal
                    task={completionTask}
                    photos={completionPhotos}
                    saving={saving}
                    photoProcessing={photoProcessing}
                    onClose={closeCompletion}
                    onComplete={completeTask}
                    onPhotos={handleFiles}
                    onOpenCamera={openCamera}
                    onRemovePhoto={removePhoto}
                    onPreview={setPreviewPhoto}
                />
            )}

            {cameraOpen && (
                <Camera setPicture={receiveCameraPhoto} setCameraOpen={setCameraOpen} />
            )}

            <PhotoLightbox src={previewPhoto} onClose={closePreview} />
        </MainDiv>
    );
};

export default ToDo;
